import { concat } from '@ember/helper';
import { registerDestructor } from '@ember/destroyable';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import type EligibilityFileJobLogModel from '../../models/eligibility-file-job-log.ts';
import EligibilityFilesDownloadEligibilityFile from './download-eligibility-file.gts';
import type Router from '@ember/routing/router-service';
import type Store from '@ember-data/store';
import UiCopyButton from '../ui/copy-button.gts';
import type Owner from '@ember/owner';
import Button from 'tio-reimagined/components/button';
import { on } from '@ember/modifier';
import { PhX, PhPlay } from 'ember-phosphor-icons';

export interface EligibilityFilesButtonOptionsSignature {
  Args: {
    companyId: string;
    legacyId: string;
    status: string;
    fileName: string;
    currentUserId: string;
    indexRoute: string;
    showRoute?: string;
    item: EligibilityFileJobLogModel;

    abortProcess?: (item: EligibilityFileJobLogModel) => void;
    beginProcess?: (item: EligibilityFileJobLogModel) => void;
  };
  Element: HTMLDivElement;
}

export default class EligibilityFilesButtonOptionsComponent extends Component<EligibilityFilesButtonOptionsSignature> {
  @service declare store: Store;
  @service declare router: Router;

  @tracked canProcess = true;
  @tracked canAbort = true;
  @tracked buttonEnabled = true;

  constructor(owner: Owner, args: EligibilityFilesButtonOptionsSignature['Args']) {
    super(owner, args);

    registerDestructor(this, () => this.args.item.stopPollingForStatus());
  }

  get processEnabled() {
    return (
      (this.args.item.canProcess && this.canProcess) === true &&
      this.reviewerProcessEnabled &&
      this.buttonEnabled
    );
  }

  get abortEnabled() {
    return (this.args.item.canAbort && this.canAbort) === true && this.buttonEnabled;
  }

  get reviewerProcessEnabled() {
    return !(
      this.args.item.status === 'REQUIRES_REVIEWER_APPROVAL' &&
      this.args.currentUserId == this.args.item.createdBy
    );
  }

  processEligibilityFile = async () => {
    this.buttonEnabled = false;
    this.canProcess = false;
    if (this.args.beginProcess) {
      await this.args.beginProcess(this.args.item);
      await this.args.item.reload();
      this.args.item.startPollingForStatus();
    }
    this.buttonEnabled = true;
  };

  abortEligibilityFile = async () => {
    this.buttonEnabled = false;
    this.canAbort = false;
    if (this.args.abortProcess) {
      await this.args.abortProcess(this.args.item);
      await this.args.item.reload();
    }
    this.buttonEnabled = true;
    this.canAbort = true;
  };

  <template>
    <div class="flex gap-x-2">
      {{#if this.processEnabled}}
        <Button
          @intent="primary"
          @appearance="outlined"
          {{on "click" this.processEligibilityFile}}
          title="Process File"
        >
          <PhPlay @size="20" />
        </Button>
      {{/if}}
      {{#if this.abortEnabled}}
        <Button
          @intent="danger"
          @appearance="outlined"
          {{on "click" this.abortEligibilityFile}}
          title="Abort Process"
        >
          <PhX @size="20" />
        </Button>
      {{/if}}
      <UiCopyButton
        @text={{@item.processId}}
        class="!relative !inline-block"
        @title={{concat "Copy: " @item.processId}}
      />
      <EligibilityFilesDownloadEligibilityFile
        @processId={{@item.processId}}
        @legacyId={{@legacyId}}
        @companyId={{@companyId}}
        @fileName={{@fileName}}
        @title="Download File"
        @format="button"
        class="!relative !inline-block"
      />
    </div>
  </template>
}
